// ── Modal и Sheet — окно и лист снизу ────────────────────────────────────────
// Одно устройство для всех всплывающих окон (раньше каждое было собрано
// вручную: свой фон, свой z-index от 100 до 10050, своя прокрутка).
//
//   <Modal title="Настройки" onClose={close} footer={<Button …/>}> … </Modal>
//   <Sheet title="Очередь" onClose={close}> … </Sheet>
//
// Что умеет само:
//   • окно не выше экрана: шапка и кнопки внизу на месте, листается середина;
//   • длинный заголовок переносится, крестик всегда виден;
//   • на телефоне Sheet — лист снизу во всю ширину (с отступом под жесты),
//     Modal — окно с полями 12 px (fullOnPhone — во весь экран, как страница);
//     на компьютере оба — окно по центру;
//   • закрывается крестиком, Esc, кнопкой «Назад» телефона и нажатием по фону
//     (dismissable={false} — только крестиком/кнопками: несохранённый ввод);
//   • страница под окном не прокручивается; мини-плеер прячется под листом.

import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SANS } from '../themes';
import { t } from '../i18n';
import IconButton from './IconButton';
import { usePalette, usePhone, Z } from './theme';
import useBackClose from './useBackClose';
import { floatSurface } from './surface';

const WIDTH = { sm: 400, md: 520, lg: 720, xl: 960 };
let locks = 0;

function lockScroll() {
  if (locks++ === 0) {
    const se = document.documentElement;
    se.dataset.acScrollLock = '1';
    document.body.style.overflow = 'hidden';
  }
  return () => {
    if (--locks === 0) {
      delete document.documentElement.dataset.acScrollLock;
      document.body.style.overflow = '';
    }
  };
}

function Dialog({ kind, title, onClose, children, footer, size = 'md', dismissable = true, closeLabel, headerExtra, bodyStyle, padded = true, fullOnPhone = false, C: Cp, labelledBy }) {
  const C = usePalette(Cp);
  const phone = usePhone();
  const titleId = useId();
  const boxRef = useRef(null);
  const downOnBackdrop = useRef(false);
  const sheet = kind === 'sheet' && phone;
  const page = fullOnPhone && phone; // на телефоне — во весь экран, как отдельная страница

  useBackClose(true, onClose);
  useEffect(lockScroll, []);
  useEffect(() => {
    if (!sheet) return undefined;
    window.dispatchEvent(new CustomEvent('ac:bottom-sheet', { detail: { open: true } }));
    return () => window.dispatchEvent(new CustomEvent('ac:bottom-sheet', { detail: { open: false } }));
  }, [sheet]);
  useEffect(() => {
    const prev = document.activeElement;
    boxRef.current?.focus({ preventScroll: true });
    return () => { try { prev?.focus?.({ preventScroll: true }); } catch { /* элемента уже нет */ } };
  }, []);

  const gutter = phone ? 12 : 24;
  const backdrop = {
    position: 'fixed', inset: 0, zIndex: Z.modal, background: 'rgba(0,0,0,.45)', display: 'flex',
    alignItems: sheet ? 'flex-end' : 'center', justifyContent: 'center',
    padding: sheet || page ? 0 : `max(${gutter}px, env(safe-area-inset-top, 0px)) ${gutter}px max(${gutter}px, env(safe-area-inset-bottom, 0px))`,
    boxSizing: 'border-box', overscrollBehavior: 'contain',
  };
  const box = {
    position: 'relative', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: sheet ? '100%' : WIDTH[size] || WIDTH.md,
    maxHeight: sheet ? '92dvh' : '100%', height: page ? '100%' : undefined, minHeight: 0, ...floatSurface(C), color: C.text, fontFamily: SANS, boxSizing: 'border-box',
    border: page ? 'none' : floatSurface(C).border, borderRadius: page ? 0 : sheet ? '16px 16px 0 0' : 14, borderBottom: sheet ? 'none' : undefined,
    paddingTop: page ? 'env(safe-area-inset-top, 0px)' : undefined,
    boxShadow: `0 24px 64px -24px ${C.shadowMd}`, outline: 'none', overflow: 'hidden',
  };
  // Шапка есть всегда, когда окно можно закрыть: у крестика своё место, и при
  // прокрутке он не ложится поверх содержимого.
  const hasHeader = title || headerExtra || onClose;
  return createPortal(
    <div style={backdrop}
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (dismissable && downOnBackdrop.current && e.target === e.currentTarget) onClose?.(); }}>
      <div ref={boxRef} role="dialog" aria-modal="true" data-panel={page ? undefined : ""} aria-labelledby={title ? (labelledBy || titleId) : undefined} tabIndex={-1} style={box}>
        {sheet && <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 2, background: C.border, margin: '8px auto 0', flexShrink: 0 }} />}
        {hasHeader && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: phone ? '12px 12px 8px 16px' : '16px 16px 10px 20px', flexShrink: 0 }}>
            <div style={{ flex: '1 1 auto', minWidth: 0, paddingTop: 7 }}>
              {title && <h2 id={titleId} style={{ margin: 0, fontSize: 17, fontWeight: 600, lineHeight: 1.3, color: C.text, overflowWrap: 'break-word' }}>{title}</h2>}
            </div>
            {headerExtra}
            {onClose && <IconButton icon="close" label={closeLabel || t("Закрыть")} variant="ghost" size={40} onClick={onClose} />}
          </div>
        )}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
          padding: padded ? (phone ? '4px 16px 16px' : '4px 20px 20px') : 0, ...bodyStyle }}>
          {children}
        </div>
        {footer && (
          <div style={{ flexShrink: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, borderTop: `1px solid ${C.borderSoft}`,
            padding: phone ? `10px 16px calc(10px + ${sheet ? 'env(safe-area-inset-bottom, 0px)' : '0px'})` : '12px 20px' }}
            className={phone ? 'ac-dialog-footer-phone' : undefined}>
            {footer}
          </div>
        )}
        {sheet && !footer && <div style={{ height: 'env(safe-area-inset-bottom, 0px)', flexShrink: 0 }} />}
      </div>
    </div>,
    document.body
  );
}

export function Modal(props) { return <Dialog kind="modal" {...props} />; }
export function Sheet(props) { return <Dialog kind="sheet" {...props} />; }
